import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Download, Smartphone, ShieldCheck, AlertTriangle,
  CheckCircle2, Loader2, Phone, Mail
} from "lucide-react";
import axios from "axios";
import { LOGO_ICON_WHITE } from "@/components/SplashScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DownloadApp() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [versionInfo, setVersionInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/app/version`)
      .then((res) => setVersionInfo(res.data))
      .catch(() => setVersionInfo({ available: false, message: "Could not reach server. Try again." }))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = () => {
    if (!versionInfo?.available) return;
    setDownloading(true);
    // Use window.location to trigger native browser download
    window.location.href = `${BACKEND_URL}/api/download/apk`;
    // Re-enable button after a few seconds (browser handles the rest)
    setTimeout(() => setDownloading(false), 5000);
  };

  const installSteps = [
    {
      num: 1,
      title: "Tap Download",
      desc: "Click the Download App button below. Your browser may ask permission to download — tap Allow.",
    },
    {
      num: 2,
      title: "Open the file",
      desc: "Once the download finishes, open the rebuilt-app.apk file from your Notifications or Downloads folder.",
    },
    {
      num: 3,
      title: "Allow install from this source",
      desc: 'Android will warn that you\'re installing from an unknown source. Tap "Settings" → enable "Allow from this source" → go back.',
    },
    {
      num: 4,
      title: "Install & open",
      desc: "Tap Install. After installation, open Rebuilt and log in with your registered mobile number.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <header
        className="px-6 py-4 text-white"
        style={{ background: "linear-gradient(135deg, #0A2540 0%, #0D47A1 60%, #1565C0 100%)" }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Back to Home</span>
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_ICON_WHITE} alt="Rebuilt" className="h-8 w-8" />
            <span className="font-bold tracking-wide">REBUILT</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-5">
            <Smartphone className="h-4 w-4" />
            Android App
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
            Get the Rebuilt App
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-10">
            Manage your EMIs, pay instantly via UPI Autopay, and track applications — all from your phone.
          </p>

          {/* Download Card */}
          <div className="max-w-xl mx-auto rounded-3xl shadow-xl border border-slate-200 bg-white p-6 sm:p-8 text-left">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-slate-500 text-sm mt-3">Checking for latest version…</p>
              </div>
            ) : versionInfo?.available ? (
              <>
                <div className="flex items-center gap-4 mb-5">
                  <div
                    className="h-14 w-14 rounded-2xl flex items-center justify-center text-white"
                    style={{ background: "linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)" }}
                  >
                    <Smartphone className="h-7 w-7" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-slate-900">Rebuilt for Android</h3>
                    <p className="text-sm text-slate-500">
                      Version {versionInfo.version_name || "—"} ({versionInfo.size_mb} MB)
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  data-testid="download-apk-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-white shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: "linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)" }}
                >
                  {downloading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Starting download…
                    </>
                  ) : (
                    <>
                      <Download className="h-5 w-5" />
                      Download App ({versionInfo.size_mb} MB)
                    </>
                  )}
                </button>

                <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    Android 7.0+
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    Signed APK
                  </div>
                  {versionInfo.sha256 && (
                    <div className="col-span-2 mt-2 break-all font-mono text-[10px] text-slate-400">
                      SHA-256: {versionInfo.sha256}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                <h3 className="font-bold text-lg text-slate-900 mb-1">App build not available yet</h3>
                <p className="text-sm text-slate-500 mb-5">
                  {versionInfo?.message ||
                    "The latest signed APK is being prepared. Please check back in a few minutes."}
                </p>
                <button
                  onClick={() => navigate("/customer/login")}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                >
                  Use Web App Instead
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Install Instructions */}
      <section className="px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">How to install</h2>
          <p className="text-slate-500 mb-6">
            Rebuilt is currently distributed directly via this website. Follow these 4 simple steps:
          </p>

          <div className="space-y-4">
            {installSteps.map((step) => (
              <div
                key={step.num}
                className="flex gap-4 p-5 bg-white rounded-2xl border border-slate-200 hover:border-blue-300 transition-colors"
              >
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center">
                  {step.num}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">{step.title}</h4>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust panel */}
          <div className="mt-8 p-5 rounded-2xl bg-green-50 border border-green-200">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-900 mb-1">Safe and signed</h4>
                <p className="text-sm text-green-800">
                  Every release is digitally signed with the official Rebuilt keystore so Android can
                  verify it hasn't been tampered with. The "Unknown source" warning is standard for
                  apps installed outside the Play Store and does not indicate a security issue.
                </p>
              </div>
            </div>
          </div>

          {/* Help */}
          <div className="mt-8 p-5 rounded-2xl bg-slate-100 border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-3">Need help installing?</h4>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="tel:7666020197"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors text-sm"
              >
                <Phone className="h-4 w-4 text-green-600" />
                7666 020 197
              </a>
              <a
                href="mailto:support@rebuiltindia.co"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors text-sm"
              >
                <Mail className="h-4 w-4 text-blue-600" />
                support@rebuiltindia.co
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
